import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectFleet, setDistance, setDuration, setMapsRecordId, setQuoteInfoRecordId, setFleetSelection, setFleetRecordWithRateId } from '../../features/bookingSlice/bookingSlice';
import PremiumLoader from "../../components/PremiumLoader";

const RightSideCars = ({ data, error }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const topRef = useRef(null);
  const bookingData = useSelector(state => state.booking);
  const { fleet_id } = useSelector(state => state.booking);

  useEffect(() => {
    if (bookingData.pickup?.coordinates && bookingData.dropoff?.coordinates && window.google) {
      const calculateAndSetRoute = async () => {
        const directionsService = new window.google.maps.DirectionsService();
        try {
          const result = await directionsService.route({
            origin: bookingData.pickup.coordinates,
            destination: bookingData.dropoff.coordinates,
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.IMPERIAL,
            region: 'US'
          });

          if (result.status === 'OK' && result.routes[0]?.legs[0]) {
            const leg = result.routes[0].legs[0];
            dispatch(setDistance(leg.distance.text));
            dispatch(setDuration(leg.duration.text));
          }
        } catch (error) {
          console.error('Error calculating directions:', error);
        }
      };
      calculateAndSetRoute();
    }
  }, [bookingData.pickup, bookingData.dropoff, dispatch]);

  const fleetData = data?.fleets ? data : (data?.data?.fleets ? data.data : data);

  useEffect(() => {
    if (fleetData?.fleets?.length > 0 && !fleet_id) {
      const firstFleet = fleetData.fleets[0];
      dispatch(selectFleet(firstFleet.fleet_id));
      dispatch(setMapsRecordId(fleetData.maps_record_id));
      dispatch(setQuoteInfoRecordId(fleetData.quote_info_record_id));
    }
  }, [fleetData, dispatch, fleet_id]);

  const handleBookingRequest = (fleet) => {
    dispatch(selectFleet(fleet.fleet_id));
    dispatch(setFleetSelection(fleet));
    if (fleet.fleet_record_with_rate_id) {
      dispatch(setFleetRecordWithRateId(fleet.fleet_record_with_rate_id));
    }
    navigate("/vehicleextra");
  };

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center py-20 px-4 text-center">
        <div className="w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400 mb-4">
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-white mb-2">Unable to Retrieve Fleet Rates</h3>
        <p className="text-sm text-slate-400 max-w-md mb-6">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold text-sm transition-all shadow-lg shadow-blue-500/25"
        >
          Retry Search
        </button>
      </div>
    );
  }

  if (!fleetData?.fleets) {
    return (
      <div className="flex flex-col justify-center items-center py-28 text-center">
        <PremiumLoader />
        <p className="mt-4 text-xs uppercase tracking-widest text-blue-400 font-bold">Retrieving Corporate Fleet Rates...</p>
      </div>
    );
  }

  const getFareDetails = (fleet) => {
    if (!fleet || !fleet.fare_details) {
      return { cardFare: '0.00', cashFare: '0.00', rawCard: 0, rawCash: 0 };
    }
    const fd = fleet.fare_details;
    // Card Fare is the standard all-inclusive fare
    const cardFareNum = parseFloat(fd.card_fare || fd.final_fare || fd.total_fare || fd.journey_fare || 0);
    // Cash Fare: if provided by backend use it, otherwise 10% cash discount
    let cashFareNum = 0;
    if (fd.cash_fare && parseFloat(fd.cash_fare) > 0) {
      cashFareNum = parseFloat(fd.cash_fare);
    } else {
      cashFareNum = Math.max(0, cardFareNum * 0.90);
    }

    return {
      cardFare: cardFareNum > 0 ? cardFareNum.toFixed(2) : '0.00',
      cashFare: cashFareNum > 0 ? cashFareNum.toFixed(2) : '0.00',
      rawCard: cardFareNum,
      rawCash: cashFareNum,
      discountVal: fd.cash_discount_val || 10
    };
  };

  return (
    <div className="w-full" ref={topRef}>
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 pb-4 border-b border-slate-800 gap-4">
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-blue-400 block mb-1">Available Fleet</span>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white">Select Your Corporate Vehicle</h2>
          <p className="text-slate-400 text-xs sm:text-sm mt-1">Guaranteed luxury, prompt arrival, and transparent dual pricing.</p>
        </div>
        <div className="text-xs text-slate-300 flex items-center gap-2 bg-slate-900/90 border border-slate-700/80 px-3 py-1.5 rounded-full shadow-sm">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span>{fleetData.fleets.length} Vehicles Available</span>
        </div>
      </div>

      {/* Luxury Fleet Grid - 2 Columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        {fleetData.fleets.map((fleet, index) => {
          const isSelected = fleet_id === fleet.fleet_id;
          const { cardFare, cashFare, discountVal } = getFareDetails(fleet);

          return (
            <div
              key={fleet.fleet_id || index}
              onClick={() => handleBookingRequest(fleet)}
              className={`group rounded-2xl transition-all duration-300 flex flex-col justify-between overflow-hidden cursor-pointer relative ${
                isSelected
                  ? 'bg-slate-900 border-2 border-blue-500 shadow-[0_12px_30px_-8px_rgba(37,99,235,0.4)] ring-1 ring-blue-400/40 -translate-y-1'
                  : 'bg-slate-900/80 border border-slate-800 hover:border-slate-700 hover:bg-slate-900 hover:shadow-xl hover:-translate-y-1'
              }`}
            >
              {/* Badge Tag */}
              {index === 0 && (
                <div className="absolute top-3.5 left-3.5 z-10">
                  <span className="px-2.5 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-blue-600 text-white shadow-md">
                    Most Popular
                  </span>
                </div>
              )}
              {index === 1 && (
                <div className="absolute top-3.5 left-3.5 z-10">
                  <span className="px-2.5 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-indigo-600 text-white shadow-md">
                    Spacious & Luxury
                  </span>
                </div>
              )}

              <div className="p-5 flex-1 flex flex-col">
                {/* Vehicle Showcase Image Container */}
                <div className="h-44 w-full flex items-center justify-center p-3 mb-4 rounded-xl bg-slate-950/80 border border-slate-800/80 group-hover:border-slate-700/80 transition-colors">
                  <img
                    src={fleet.info?.img_link || fleet.image_url || 'https://dispatch.bostonairportexpresscar.com/assets/images/fleet/1.png'}
                    alt={fleet.name || 'Executive Vehicle'}
                    className="max-h-full max-w-full object-contain filter drop-shadow-lg group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = 'https://dispatch.bostonairportexpresscar.com/assets/images/fleet/1.png';
                    }}
                  />
                </div>

                {/* Vehicle Name & Class */}
                <div className="mb-3">
                  <h3 className="font-bold text-base text-white group-hover:text-blue-300 transition-colors tracking-tight line-clamp-1">
                    {fleet.name || "Corporate Luxury Vehicle"}
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {fleet.model || "Executive Business Class"}
                  </p>
                </div>

                {/* Capacity Badges */}
                <div className="flex items-center gap-4 py-2 px-3 rounded-lg bg-slate-950/60 border border-slate-800/80 text-xs text-slate-300 mb-4">
                  <div className="flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-blue-400 !text-[17px]">person</span>
                    <span>{fleet.passengers || 3} Guests</span>
                  </div>
                  <span className="text-slate-700">•</span>
                  <div className="flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-blue-400 !text-[17px]">luggage</span>
                    <span>{fleet.suitcases || fleet.luggage || 3} Luggage</span>
                  </div>
                </div>

                {/* Dual Pricing Boxes */}
                <div className="grid grid-cols-2 gap-2.5 mt-auto pt-1">
                  {/* By Cash Pill */}
                  <div className="p-3 rounded-xl bg-emerald-950/30 border border-emerald-500/25 flex flex-col justify-between">
                    <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-emerald-400">
                      <span className="material-symbols-outlined !text-[14px]">payments</span>
                      <span>By Cash</span>
                    </div>
                    <div className="mt-1">
                      <div className="text-xl font-black text-emerald-200 font-mono tracking-tight">
                        ${cashFare}
                      </div>
                      <span className="text-[10px] text-emerald-400/90 font-medium">
                        {discountVal}% Cash OFF
                      </span>
                    </div>
                  </div>

                  {/* By Card Pill */}
                  <div className="p-3 rounded-xl bg-blue-950/30 border border-blue-500/25 flex flex-col justify-between">
                    <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-blue-400">
                      <span className="material-symbols-outlined !text-[14px]">credit_card</span>
                      <span>By Card</span>
                    </div>
                    <div className="mt-1">
                      <div className="text-xl font-black text-blue-200 font-mono tracking-tight">
                        ${cardFare}
                      </div>
                      <span className="text-[10px] text-blue-400/90 font-medium">
                        Standard Fare
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="p-4 bg-slate-950/80 border-t border-slate-800/80">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBookingRequest(fleet);
                  }}
                  className={`w-full py-3 px-4 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-2 ${
                    isSelected
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                      : 'bg-blue-600/20 hover:bg-blue-600 text-blue-300 hover:text-white border border-blue-500/30 hover:border-transparent'
                  }`}
                >
                  <span>{isSelected ? 'Selected' : 'Select Vehicle'}</span>
                  <span className="material-symbols-outlined !text-[16px]">arrow_forward</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RightSideCars;
